import React, { useState, useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import useBusinessResource from "../../../hooks/useBusinessResource";
import useIsMobile from "../../../hooks/useIsMobile";
import { invalidateBusinessResources } from "../../../utils/businessResourceFreshness";
import { useSelection } from "../../../SelectionContext";
import {
  Modal,
  Tabs,
  Table,
  Form,
  Input,
  InputNumber,
  Select,
  Button,
  Space,
  Typography,
  Tag,
  message,
  Popconfirm,
  Tooltip,
  Alert,
  Divider,
  Switch,
  DatePicker,
  Collapse,
  theme,
} from "antd";
import {
  BarChartOutlined,
  UserOutlined,
  ClockCircleOutlined,
  GiftOutlined,
  MinusCircleOutlined,
  SaveOutlined,
  ReloadOutlined,
  DownloadOutlined,
  UploadOutlined,
  CalculatorOutlined,
  TeamOutlined,
  DollarOutlined,
  EditOutlined,
  DeleteOutlined,
  PlusOutlined,
  BulbOutlined,
  FileTextOutlined,
  CalendarOutlined,
  FullscreenOutlined,
  FullscreenExitOutlined,
  InfoCircleOutlined,
  RightOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import { usePayrollProcessing } from "../store/context";
import {
  PayrollPeriod,
  PayrollEmployee,
  PayrollVariable,
} from "../../../types/hr";
import { formatCurrency, formatPeriod } from "../utils";
import BulkVariablesModal from "./BulkVariablesModal";
import QuickAddVariableModal from "./QuickAddVariableModal";
import PayslipDetailsModal from "./PayslipDetailsModal";
import { useAuth } from "../../../context/AuthContext";
import RowActionsMenu from "../../../components/RowActionsMenu";
import "./VariableManagementModal.css";

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

interface VariableManagementModalProps {
  visible: boolean;
  period: PayrollPeriod | null;
  onCancel: () => void;
  onSuccess: () => void;
}

const getPeriodStatusLabel = (period: PayrollPeriod | null) => {
  if (!period) return "Brouillon";

  const totalCalculations = Number(
    period.total_calculations || period.total_employees || 0
  );
  const validatedCalculations = Number(period.validated_calculations || 0);
  const isValidationInProgress =
    period.status === "calculated" &&
    validatedCalculations > 0 &&
    validatedCalculations < totalCalculations;

  if (isValidationInProgress) return "Validation en cours";

  const labels: Record<string, string> = {
    draft: "Brouillon",
    variables_set: "Variables saisies",
    processing: "En Traitement",
    calculated: "Calculé",
    partial_error: "Erreurs Partielles",
    validated: "Validé",
    paid: "Payé",
    closed: "Fermé",
    archived: "Archivé",
  };

  return labels[period.status] || period.status;
};

interface EmployeeVariable {
  employee_id: number;
  employee_name: string;
  employee_number: string;
  department_name?: string;
  position_name?: string;
  base_salary: number;

  // Éléments de paie assignés (nouvelle approche)
  assigned_payroll_items?: AssignedPayrollItem[];
  calculated_base_salary?: number;
  calculated_allowances?: number;
  calculated_deductions?: number;

  // Variables de présence
  worked_days: number;
  sick_days: number;
  vacation_days: number;
  unpaid_leave_days: number;

  // Heures supplémentaires
  overtime_hours: number;
  overtime_rate: number;

  // Primes et indemnités (calculées depuis les éléments assignés)
  bonuses: number;
  allowances: number;
  commissions: number;

  // Déductions (calculées depuis les éléments assignés)
  advances: number;
  loans_deduction: number;
  other_deductions: number;

  // Autres
  dependents: number;
  notes?: string;
}

interface AssignedPayrollItem {
  id: number;
  employee_id: number;
  payroll_item_id: number;
  code: string;
  name: string;
  type: "gain" | "deduction" | "info";
  category: string;
  calculation_type: "fixed" | "percentage" | "formula";
  default_amount: number;
  default_percentage: number;
  formula?: string;
  custom_amount?: number;
  custom_percentage?: number;
  calculated_value?: number;
  is_active: boolean;
  start_date?: string;
  end_date?: string;
  source_fingerprint?: string;
  monthly_salary_reference?: number;
  min_amount?: number;
  max_amount?: number;
  employee_context?: Partial<PayrollEmployee> & { dependents_count?: number; professional_category?: string };
}

interface PayrollItem {
  id: number;
  code: string;
  name: string;
  type: "gain" | "deduction" | "info";
  category: string;
  calculation_type: "fixed" | "percentage" | "formula";
  base_amount: number;
  percentage: number;
}

interface PreparationIssue {
  employee: PayrollEmployee;
  message: string;
  code?: string;
  variable_type?: string;
  payroll_item_id?: number;
}

const VariableManagementModal: React.FC<VariableManagementModalProps> = ({
  visible,
  period,
  onCancel,
  onSuccess,
}) => {
  const { state } = usePayrollProcessing();
  const { token, user } = useAuth();
  const { selectedCompany } = useSelection();
  const queryClient = useQueryClient();
  const { token: themeToken } = theme.useToken();
  const isMobile = useIsMobile();
  const [expanded, setExpanded] = useState(false);
  const [tableBodyHeight, setTableBodyHeight] = useState(320);
  const tableViewportRef = useRef<HTMLDivElement>(null);
  // Payroll is company scoped, without a warehouse dependency.
  const resourceScope = { userId: user?.id, companyId: period?.company_id, warehouseId: "company", moduleScope: "hr" };

  // États locaux
  const [loading, setLoading] = useState(false);
  const [dataError, setDataError] = useState<string | null>(null);
  const [dataReady, setDataReady] = useState(false);
  const [preparationIssues, setPreparationIssues] = useState<PreparationIssue[]>([]);
  const [quickAddDefaults, setQuickAddDefaults] = useState<{ code?: string; variable_type?: string; payroll_item_id?: number }>({});
  const loadVersion = useRef(0);
  const activeKey = useRef<string | null>(null);
  activeKey.current = visible && period ? `${period.id}:${period.company_id}:${user?.id}:${selectedCompany}` : null;
  const [periodEmployees, setPeriodEmployees] = useState<PayrollEmployee[]>([]);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("bonuses"); // Changé de "overview" à "bonuses" pour voir les primes directement
  const [employeeVariables, setEmployeeVariables] = useState<
    EmployeeVariable[]
  >([]);
  const [payrollItems, setPayrollItems] = useState<PayrollItem[]>([]);
  const [employeeAssignedItems, setEmployeeAssignedItems] = useState<
    { employee_id: number; assignedItems: AssignedPayrollItem[] }[]
  >([]);
  const [editingKey, setEditingKey] = useState("");
  const [form] = Form.useForm();

  useEffect(() => {
    if (!visible) setExpanded(false);
  }, [visible]);

  useEffect(() => {
    if (!visible) return;
    const viewport = tableViewportRef.current;
    if (!viewport) return;
    const measure = () => {
      if (!viewport.clientHeight) return;
      const header = viewport.querySelector<HTMLElement>(".ant-table-header");
      const body = viewport.querySelector<HTMLElement>(".ant-table-body");
      const scrollbarHeight = body ? Math.max(0, body.offsetHeight - body.clientHeight) : 0;
      const height = Math.max(96, Math.floor(viewport.clientHeight - (header?.getBoundingClientRect().height || 44) - scrollbarHeight - 2));
      setTableBodyHeight(previous => previous === height ? previous : height);
    };
    const frame = window.requestAnimationFrame(measure);
    const observer = typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
    observer?.observe(viewport);
    window.addEventListener("resize", measure);
    return () => {
      window.cancelAnimationFrame(frame);
      observer?.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [visible, period?.id, activeTab, expanded, dataError]);

  // Nouveaux états pour les modals
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [showQuickAddModal, setShowQuickAddModal] = useState(false);
  const [selectedEmployee, setSelectedEmployee] =
    useState<PayrollEmployee | null>(null);

  // États pour les modals de détail des éléments assignés
  const [showBonusDetailModal, setShowBonusDetailModal] = useState(false);
  const [showAllowancesDetailModal, setShowAllowancesDetailModal] =
    useState(false);
  const [selectedEmployeeForDetail, setSelectedEmployeeForDetail] =
    useState<EmployeeVariable | null>(null);

  // États pour le modal de bulletin IUTS
  const [showPayslipModal, setShowPayslipModal] = useState(false);
  const [selectedEmployeeForPayslip, setSelectedEmployeeForPayslip] =
    useState<EmployeeVariable | null>(null);

  // Fonction utilitaire pour déterminer la vraie catégorie d'un élément de paie
  const getActualCategory = (
    item: AssignedPayrollItem
  ): "bonus" | "allowance" | "commission" | "advance" | "loan" | "other" => {
    // Exclure explicitement SAL_BASE des indemnités (ce n'est pas une indemnité)
    if (item.code === "SAL_BASE") {
      return "other";
    }

    if (item.type === "gain") {
      // Logique intelligente pour les gains
      if (item.code.startsWith("PRIME_") || item.category === "bonus") {
        return "bonus";
      } else if (
        item.code.startsWith("COMM_") ||
        item.category === "commission"
      ) {
        return "commission";
      } else if (
        item.code.startsWith("IND_") ||
        item.code.startsWith("INDEM_") ||
        (item.category === "allowance" && !item.code.startsWith("PRIME_"))
      ) {
        return "allowance";
      } else {
        return "other"; // Ne plus traiter par défaut comme allowance
      }
    } else if (item.type === "deduction") {
      if (item.category === "advance") {
        return "advance";
      } else if (item.category === "loan") {
        return "loan";
      } else {
        return "other";
      }
    }
    return "other";
  };

  const preparedSource = useRef<string | null>(null);
  const bulkSource = useRef<string | null>(null);
  const editingRef = useRef(editingKey);
  editingRef.current = editingKey;
  const resource = useBusinessResource({
    ...resourceScope, resource: "payroll-preparation", parameters: [period?.id, period?.start_date, period?.end_date],
    enabled: Boolean(visible && token && period && Number(selectedCompany) === Number(period.company_id)), active: visible,
    load: async signal => {
      if (!period) throw new Error("Période manquante");
      let variablesRevision: string | undefined;
      const read = async (url: string) => {
        const response = await fetch(url, { signal, headers: { Authorization: `Bearer ${token}`, "X-Company-Id": String(period.company_id) } });
        const result = await response.json();
        if (url.endsWith('/variables') && typeof result.variables_revision === 'string') variablesRevision = result.variables_revision;
        if (!response.ok || !result.success || !Array.isArray(result.data)) throw Object.assign(new Error(result.message || result.error || "Lecture de paie impossible"), {
          status: response.status, code: result.code, variable_type: result.variable_type, payroll_item_id: result.payroll_item_id,
        });
        if (result.period && (String(result.period.start_date).slice(0,10) !== String(period.start_date).slice(0,10) ||
            String(result.period.end_date).slice(0,10) !== String(period.end_date).slice(0,10))) throw new Error('Les dates de cette période ont changé. Rouvrez la préparation.');
        return result.data;
      };
      const [employees, variables, catalog] = await Promise.all([
        read(`/api/hr/payroll-processing/periods/${period.id}/employees`),
        read(`/api/hr/payroll-processing/periods/${period.id}/variables`), read("/api/hr/payroll-items"),
      ]);
      const assignments = await Promise.all(employees.map(async (employee: PayrollEmployee) => {
        let rows;
        try { rows = await read(`/api/hr/payroll-items/employee/${employee.employee_id}?period_id=${period.id}`); }
        catch (error) {
          const issue = error as Error & { status?: number; code?: string; variable_type?: string; payroll_item_id?: number };
          if (issue.status !== 409) throw error;
          return { employee_id: employee.employee_id, assignedItems: [], issue: {
            employee, message: issue.message, code: issue.code, variable_type: issue.variable_type, payroll_item_id: issue.payroll_item_id,
          } };
        }
        if (rows.some((item: AssignedPayrollItem) => Number(item.employee_id) !== Number(employee.employee_id))) throw new Error("Des éléments reçus appartiennent à un autre employé");
        return { employee_id: employee.employee_id, assignedItems: rows.filter((item: AssignedPayrollItem) => Number(item.is_active) === 1 &&
          (!item.start_date || String(item.start_date).slice(0,10) <= String(period.end_date).slice(0,10)) &&
          (!item.end_date || String(item.end_date).slice(0,10) >= String(period.start_date).slice(0,10))) };
      }));
      return { employees: employees as PayrollEmployee[], variables, variablesRevision, catalog, assignments };
    },
  });

  useEffect(() => {
    preparedSource.current = null; setDataReady(false); setDataError(null);
    setEmployeeVariables([]); setPeriodEmployees([]); setEditingKey("");
    setPreparationIssues([]); setShowBulkModal(false); bulkSource.current = null;
    setShowQuickAddModal(false); setSelectedEmployee(null); setQuickAddDefaults({});
    return () => { loadVersion.current++; };
  }, [visible, period?.id, period?.company_id, user?.id, selectedCompany]);

  useEffect(() => {
    if (!visible || !period) return;
    if (Number(selectedCompany) !== Number(period.company_id)) {
      setDataReady(false); setLoading(false);
      setDataError('L’entreprise sélectionnée a changé. Rouvrez la préparation dans le contexte de cette entreprise.');
      return;
    }
    if (resource.error) {
      setDataReady(false); setLoading(false);
      setDataError(resource.error instanceof Error ? resource.error.message : "Lecture de paie impossible. Actualisez pour réessayer.");
      return;
    }
    if (resource.status !== "verified" || !resource.data) { setLoading(resource.loading); return; }
    const data = resource.data;
    const signature = JSON.stringify(data);
    if (editingRef.current && preparedSource.current !== signature) {
      setDataReady(false); setLoading(false);
      setDataError("Les données de paie ont changé pendant la saisie. Vos valeurs sont conservées ; enregistrez ou annulez la ligne, puis actualisez.");
      return;
    }
    if (editingRef.current) { setLoading(false); return; }
    const version = ++loadVersion.current, key = activeKey.current;
    const controller = new AbortController();
    setLoading(true); setDataReady(false);
    setPeriodEmployees(data.employees); setEmployeeAssignedItems(data.assignments); setPayrollItems(data.catalog);
    const issues = data.assignments.flatMap(row => row.issue ? [row.issue] : []);
    setPreparationIssues(issues);
    void processVariablesData(data.variables, data.assignments, data.employees.filter(employee => !issues.some(issue => Number(issue.employee.employee_id) === Number(employee.employee_id))),
      () => loadVersion.current === version && activeKey.current === key, controller.signal).then(() => {
      if (loadVersion.current === version && activeKey.current === key) {
        preparedSource.current = signature; setDataReady(issues.length === 0);
        setDataError(issues.length ? `${issues.length} employé(s) nécessitent des données propres à cette période. Les autres employés restent affichés ; la génération du snapshot est bloquée jusqu’à résolution.` : null);
      }
    }).catch(error => {
      if (loadVersion.current === version && activeKey.current === key) {
        setDataError(error instanceof Error ? error.message : "Erreur lors de la préparation de paie");
      }
    }).finally(() => { if (loadVersion.current === version && activeKey.current === key) setLoading(false); });
    return () => { controller.abort(); if (loadVersion.current === version) loadVersion.current++; };
  }, [visible, selectedCompany, resource.data, resource.status, resource.error]);

  const loadData = async () => {
    try { await resource.refresh({ force: true }); }
    catch (error) { setDataReady(false); setDataError(error instanceof Error ? error.message : "Lecture de paie impossible"); }
  };
  const loadVariables = async () => {
    await invalidateBusinessResources(queryClient, resourceScope, "payroll-variables");
    await resource.refresh({ force: true });
  };

  const processVariablesData = async (
    variables: any[],
    assignedItemsData?: {
      employee_id: number;
      assignedItems: AssignedPayrollItem[];
    }[],
    employees: PayrollEmployee[] = periodEmployees,
    isCurrent: () => boolean = () => true,
    signal?: AbortSignal
  ) => {
    const aliases: Record<string, string> = { worked_days: 'attendance_days', heures_sup: 'overtime_hours',
      taux_heure_sup: 'overtime_rate', avance: 'advance', pret: 'loan_deduction', autres_deductions: 'other_deduction' };
    variables = variables.map(variable => ({ ...variable, variable_type: aliases[variable.variable_type] || variable.variable_type }));
    // Utiliser les données passées en paramètre ou celles du state
    const employeeAssignedItemsToUse =
      assignedItemsData || employeeAssignedItems;

    // Créer une structure de données pour les variables par employé
    const key = activeKey.current;
    const employeeVarsMap = new Map<number, Partial<EmployeeVariable>>();

    // Initialiser avec les employés de la période - UTILISER UNE APPROCHE ASYNCHRONE
    const processEmployeesAsync = async () => {
      for (const emp of employees) {
        // Récupérer les éléments de paie assignés pour cet employé avec vérification stricte
        const employeeAssigned = employeeAssignedItemsToUse.find(
          (item) => item.employee_id === emp.employee_id
        );

        if (employeeAssigned) {
          // Vérification de sécurité supplémentaire
          const invalidItems = employeeAssigned.assignedItems.filter(
            (item) => item.employee_id && item.employee_id !== emp.employee_id
          );

          if (invalidItems.length > 0) {
            // Éléments mal assignés détectés
          }
        }

        // Créer une copie profonde des éléments assignés pour éviter les références partagées
        const assignedItemsCopy = employeeAssigned?.assignedItems
          ? JSON.parse(JSON.stringify(employeeAssigned.assignedItems))
          : [];

        // S'assurer que tous les éléments ont le bon employee_id
        assignedItemsCopy.forEach((item: AssignedPayrollItem) => {
          if (Number(item.employee_id) !== Number(emp.employee_id)) throw new Error("Élément de paie mal assigné");
        });
        const periodEmployeeContext = assignedItemsCopy.find((item: AssignedPayrollItem) => item.employee_context)?.employee_context;
        const employeeForPeriod = { ...emp, ...periodEmployeeContext };

        // Calculer les valeurs basées sur les éléments assignés AVEC évaluation des formules
        const calculatedValues = await calculateValuesFromAssignedItems(
          assignedItemsCopy,
          { ...employeeForPeriod, ...Object.fromEntries(variables.filter(variable => Number(variable.employee_id) === Number(emp.employee_id) && variable.payroll_item_id == null &&
            (variable.variable_type !== 'dependents' || variable.dependents_source === 'period_override' || variable.historical_employee_context))
            .map(variable => [variable.variable_type === "attendance_days" ? "worked_days" : variable.variable_type === 'salaire_base' ? 'base_salary' : variable.variable_type, Number(variable.value)])) }, signal
        );

        const employeeData: Partial<EmployeeVariable> = {
          employee_id: emp.employee_id,
          employee_name: `${emp.first_name} ${emp.last_name}`,
          employee_number: emp.employee_number,
          department_name: emp.department_name,
          position_name: emp.position_name,
          base_salary: calculatedValues.baseSalary,

          // Utiliser les éléments assignés comme source principale (copie profonde)
          assigned_payroll_items: assignedItemsCopy,
          calculated_base_salary: calculatedValues.baseSalary,
          calculated_allowances: calculatedValues.totalAllowances,
          calculated_deductions: calculatedValues.totalDeductions,

          // Variables de présence (valeurs par défaut)
          worked_days: 30,
          sick_days: 0,
          vacation_days: 0,
          unpaid_leave_days: 0,
          overtime_hours: 0,
          overtime_rate: 1.5,

          // Utiliser les valeurs calculées depuis les éléments assignés
          bonuses: calculatedValues.bonuses,
          allowances: calculatedValues.totalAllowances,
          commissions: calculatedValues.commissions,
          advances: calculatedValues.advances,
          loans_deduction: calculatedValues.loanDeductions,
          other_deductions: calculatedValues.otherDeductions,
          dependents: Number(periodEmployeeContext
            ? periodEmployeeContext.dependents_count ?? 0
            : (emp as PayrollEmployee & { dependents_count?: number }).dependents_count ?? 0),
        };

        employeeVarsMap.set(emp.employee_id, employeeData);
      }

      // Appliquer les variables existantes (qui peuvent surcharger les valeurs calculées)
      variables.forEach((variable: any) => {
        const employeeVars = employeeVarsMap.get(variable.employee_id);
        if (employeeVars) {
          // Mapper les types de variables aux propriétés
          switch (variable.variable_type) {
            case "attendance_days":
              employeeVars.worked_days = Number(variable.value);
              break;
            case "sick_days":
              employeeVars.sick_days = Number(variable.value);
              break;
            case "vacation_days":
              employeeVars.vacation_days = Number(variable.value);
              break;
            case "unpaid_leave":
              employeeVars.unpaid_leave_days = Number(variable.value);
              break;
            case "overtime_hours":
              employeeVars.overtime_hours = Number(variable.value);
              break;
            case "overtime_rate":
              employeeVars.overtime_rate = Number(variable.value);
              break;
            // CORRECTION: Pour les gains/déductions, ne PAS additionner si déjà calculés depuis les éléments assignés
            // Les variables manuelles ne doivent être ajoutées que si elles ne sont pas déjà incluses dans les éléments assignés
            case "bonus":
              // Les primes sont déjà calculées depuis les éléments assignés, ne pas doubler
              if (variable.payroll_item_id == null) employeeVars.bonuses = Number(employeeVars.bonuses || 0) + Number(variable.value);
              break;
            case "allowance":
              // Les indemnités sont déjà calculées depuis les éléments assignés, ne pas doubler  
              if (variable.payroll_item_id == null) employeeVars.allowances = Number(employeeVars.allowances || 0) + Number(variable.value);
              break;
            case "commission":
              // Les commissions sont déjà calculées depuis les éléments assignés, ne pas doubler
              if (variable.payroll_item_id == null) employeeVars.commissions = Number(employeeVars.commissions || 0) + Number(variable.value);
              break;
            case "advance":
              // Les avances sont déjà calculées depuis les éléments assignés, ne pas doubler
              if (variable.payroll_item_id == null) employeeVars.advances = Number(variable.value);
              break;
            case "loan_deduction":
              // Les prêts sont déjà calculés depuis les éléments assignés, ne pas doubler
              if (variable.payroll_item_id == null) employeeVars.loans_deduction = Number(variable.value);
              break;
            case "other_deduction":
              // Les autres déductions sont déjà calculées depuis les éléments assignés, ne pas doubler
              if (variable.payroll_item_id == null) employeeVars.other_deductions = Number(variable.value);
              break;
            case "dependents":
              if (variable.dependents_source === 'period_override' || variable.historical_employee_context) {
                employeeVars.dependents = Number(variable.value);
              }
              break;
          }

          if (variable.description && !employeeVars.notes) {
            employeeVars.notes = variable.description;
          }
        }
      });

      const finalEmployeeVariables = Array.from(
        employeeVarsMap.values()
      ) as EmployeeVariable[];

      // Variables de paie calculées

      // Vérification d'intégrité finale
      const integrityIssues: string[] = [];

      finalEmployeeVariables.forEach((empVar) => {
        // Vérifier que employee_id est cohérent
        if (!empVar.employee_id) {
          integrityIssues.push(
            `Employee ID manquant pour ${empVar.employee_name}`
          );
        }

        // Vérifier que les éléments assignés appartiennent bien à cet employé
        if (empVar.assigned_payroll_items) {
          const invalidItems = empVar.assigned_payroll_items.filter(
            (item) =>
              item.employee_id && item.employee_id !== empVar.employee_id
          );
          if (invalidItems.length > 0) {
            integrityIssues.push(
              `Éléments mal assignés pour ${
                empVar.employee_name
              }: ${invalidItems.map((item) => item.code).join(", ")}`
            );
          }
        }
      });

      if (integrityIssues.length > 0) {
        message.error(
          `${integrityIssues.length} problème(s) d'intégrité détecté(s).`
        );
      }

      if (activeKey.current === key && isCurrent()) setEmployeeVariables(finalEmployeeVariables);
    };

    await processEmployeesAsync();
  };

  // Fonction pour calculer les valeurs basées uniquement sur les éléments assignés
  const calculateValuesFromAssignedItems = async (
    assignedItems: AssignedPayrollItem[],
    employeeData?: PayrollEmployee,
    signal?: AbortSignal
  ) => {
    const checkAmountBounds = (item: AssignedPayrollItem, amount: number) => {
      if (!Number.isFinite(amount) || amount < 0) throw new Error(`Montant invalide pour ${item.code}`);
      for (const [bound, direction] of [[item.min_amount, 'minimum'], [item.max_amount, 'maximum']] as const) {
        if (bound == null) continue;
        const limit = Number(bound);
        if (!Number.isFinite(limit) || limit < 0) throw new Error(`Borne ${direction} invalide pour ${item.code}`);
        if (direction === 'minimum' ? amount < limit : amount > limit) {
          throw new Error(`Le montant calculé de ${item.code} (${amount}) dépasse sa borne ${direction} (${limit}). Corrigez la rubrique avant de préparer la paie.`);
        }
      }
    };
    // Calcul des valeurs pour les éléments de paie assignés

    let baseSalaryCalculated = 0;
    let overtimeBaseSalary = 0;
    let totalAllowances = 0;
    let totalDeductions = 0;
    let bonuses = 0;
    let commissions = 0;
    let advances = 0;
    let loanDeductions = 0;
    let otherDeductions = 0;

    // Vérification de sécurité : s'assurer que tous les éléments ont le même employee_id
    if (assignedItems.length > 0) {
      const firstEmployeeId = assignedItems[0].employee_id;
      const inconsistentItems = assignedItems.filter(
        (item) => item.employee_id !== firstEmployeeId
      );

      if (inconsistentItems.length > 0) {
        // Éléments avec des employee_id incohérents détectés
      }
    }

    // Première passe : identifier le salaire de base assigné
    const baseSalaryItem = assignedItems.find(
      (item) => item.is_active && item.code === "SAL_BASE"
    );

    if (baseSalaryItem) {
      const historical = Boolean(period && String(period.end_date).slice(0,10) < new Date().toISOString().slice(0,10));
      if (historical && employeeData?.base_salary == null && baseSalaryItem.custom_amount == null) throw new Error("Salaire historique indisponible pour cette période");
      const reference = Number(baseSalaryItem.monthly_salary_reference ?? baseSalaryItem.custom_amount ??
        (baseSalaryItem.calculation_type === 'formula' ? (Number(baseSalaryItem.default_amount) || employeeData?.base_salary || 0) : baseSalaryItem.default_amount) ?? 0);
      overtimeBaseSalary = reference;
      if (baseSalaryItem.calculation_type === 'formula') baseSalaryCalculated = await evaluateFormulaForEmployee(baseSalaryItem, employeeData, reference, signal);
      else if (baseSalaryItem.calculation_type === 'percentage') baseSalaryCalculated = reference * Number(baseSalaryItem.custom_percentage ?? baseSalaryItem.default_percentage ?? 100) / 100;
      else baseSalaryCalculated = reference;
      baseSalaryItem.calculated_value = baseSalaryCalculated;
      checkAmountBounds(baseSalaryItem, baseSalaryCalculated);
    } else {
      baseSalaryCalculated = Number(employeeData?.base_salary ?? 0);
      overtimeBaseSalary = baseSalaryCalculated;
    }
    if (!Number.isFinite(baseSalaryCalculated) || baseSalaryCalculated < 0) throw new Error('Montant invalide pour SAL_BASE');

    // Deuxième passe : calculer tous les autres éléments
    for (const item of assignedItems) {
      if (!item.is_active || item.code === 'SAL_BASE') continue;
      if (item.calculation_type === 'formula' && !item.formula?.trim() && ['HS_15','HS_35'].includes(item.code)) {
        item.formula = `OVERTIME_CALC(overtime_hours_${item.code === 'HS_15' ? '15' : '35'}, ${item.code === 'HS_15' ? '1.15' : '1.35'}, base_salary)`;
      }
      if (['CNSS','CNSS_EMP','CNSS_EMPLOYE','CNSS_PAT','CNSS_EMPLOYER','IUTS','IUTS_NET','IUTS_ZERO_CHARGE','IRPP','FSP'].includes(item.code.toUpperCase())) {
        item.calculated_value = 0; continue; // Derived server contributions are not manual inputs.
      }

      let calculatedValue = 0;

      // Calculer la valeur selon le type de calcul (forcer conversion en nombre)
      switch (item.calculation_type) {
        case "fixed":
          calculatedValue = Number(
            item.custom_amount ?? item.default_amount ?? 0
          );
          break;
        case "percentage":
          const percentage = Number(
            item.custom_percentage ?? item.default_percentage ?? 0
          );
          // Utiliser le salaire de base calculé depuis les éléments assignés
          calculatedValue = Number(baseSalaryCalculated) * (percentage / 100);
          break;
        case "formula":
          // Évaluation des formules avec paramètres de législation
          try {
            calculatedValue = await evaluateFormulaForEmployee(
              item,
              employeeData,
              ['HS_15', 'HS_35'].includes(item.code.toUpperCase()) ? overtimeBaseSalary : baseSalaryCalculated, signal
            );
            
          } catch (error) {
            throw new Error(`La formule ${item.code} n’a pas pu être calculée : ${error instanceof Error ? error.message : "formule invalide"}. Corrigez-la avant d’enregistrer le snapshot.`);
          }
          break;
      }

      checkAmountBounds(item, calculatedValue);
      // Mettre à jour la valeur calculée dans l'objet
      item.calculated_value = calculatedValue;

      // Répartir selon le type d'élément (exclure SAL_BASE déjà traité)
      if (item.code === "SAL_BASE") {
        continue;
      }

      if (item.type === "gain") {
        // Logique intelligente de catégorisation basée sur le code
        const isPrime =
          item.code.startsWith("PRIME_") || item.category === "bonus";
        const isCommission =
          item.code.startsWith("COMM_") || item.category === "commission";
        const isAllowance =
          item.code.startsWith("IND_") ||
          item.code.startsWith("INDEM_") ||
          (item.category === "allowance" && !isPrime && !isCommission);

        if (isPrime) {
          bonuses = Number(bonuses) + Number(calculatedValue);
        } else if (isCommission) {
          commissions = Number(commissions) + Number(calculatedValue);
        } else if (isAllowance) {
          totalAllowances = Number(totalAllowances) + Number(calculatedValue);
        }
      } else if (item.type === "deduction") {
        switch (item.category) {
          case "advance":
            advances = Number(advances) + Number(calculatedValue);
            break;
          case "loan":
            loanDeductions = Number(loanDeductions) + Number(calculatedValue);
            break;
          default:
            otherDeductions = Number(otherDeductions) + Number(calculatedValue);
            break;
        }
        totalDeductions = Number(totalDeductions) + Number(calculatedValue);
      }
    }

    const result = {
      baseSalary: baseSalaryCalculated,
      totalAllowances,
      totalDeductions,
      bonuses,
      commissions,
      advances,
      loanDeductions,
      otherDeductions,
    };

    // Vérification de cohérence finale
    const expectedTotal = bonuses + totalAllowances + commissions;
    const expectedDeductionsTotal = advances + loanDeductions + otherDeductions;

    return result;
  };

  // Nouvelle fonction pour évaluer les formules en utilisant l'API d'évaluation
  const evaluateFormulaForEmployee = async (
    item: AssignedPayrollItem,
    employeeData: PayrollEmployee | undefined,
    baseSalary: number,
    signal?: AbortSignal
  ): Promise<number> => {
    if (!item.formula || !employeeData) {
      console.warn(`⚠️ Formule ou employé manquant pour ${item.code}, utilisation default_amount`);
      throw new Error(`Formule ou employé manquant pour ${item.code}`);
    }

    try {
      // Appeler l'API d'évaluation de formules
      const response = await fetch("/api/hr/formulas/evaluate", {
        signal,
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
            "X-Company-Id": String(period?.company_id),
        },
        body: JSON.stringify({
          formula: item.formula,
          variables: {
            hire_date: employeeData.hire_date,
            base_salary: baseSalary,
            employee_id: employeeData.employee_id,
            ...(() => {
              const monthly = employeeData as PayrollEmployee & { worked_days?: number; overtime_hours?: number; overtime_rate?: number; unpaid_leave?: number };
              const hours = monthly.overtime_hours ?? 0, rate = monthly.overtime_rate ?? 1.5;
              return { worked_days: monthly.worked_days ?? 30, overtime_hours: hours,
                overtime_hours_15: Number(rate) === 1.15 ? hours : 0, overtime_hours_35: Number(rate) === 1.35 ? hours : 0,
                overtime_rate: rate, unpaid_leave_days: monthly.unpaid_leave ?? 0 };
            })(),
          },
          period_id: period?.id,
          // CORRECTION: Passer la date de fin de période pour cohérence temporelle
          calculation_date: period?.end_date || null,
        }),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.message || "Erreur lors du calcul de la formule");
      }

      const result = await response.json();

      if (result.success) {
        const raw = result.data?.result;
        if (!['number', 'string'].includes(typeof raw) || (typeof raw === 'string' && !raw.trim()) || !Number.isFinite(Number(raw)) || Number(raw) < 0) {
          throw new Error('Résultat de formule absent ou invalide');
        }
        const calculatedValue = Number(raw);
        return calculatedValue;
      } else {
        throw new Error(result.error || "Erreur inconnue");
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      console.error(`❌ API formule échouée pour ${item.code}:`, errorMessage);
      // CORRECTION CRITIQUE: Ne jamais utiliser custom_amount comme fallback pour les formules
      // Relancer l'erreur pour que le catch du niveau supérieur gère avec default_amount
      throw new Error(`Échec évaluation formule ${item.code}: ${errorMessage}`);
    }
  };

  // Nouvelle fonction pour gérer les opérations en masse
  const handleBulkOperation = async (operation: any) => {
    if (!period?.id) return;

    try {
      setSaving(true);

      if (!token) {
        throw new Error("Session expirée. Veuillez vous reconnecter.");
      }

      const expectedSource = bulkSource.current;
      if (!expectedSource) throw new Error("Les données de paie sont indisponibles. Rouvrez les opérations en masse après actualisation.");
      const latest = await resource.refresh({ force: true });
      if (JSON.stringify(latest) !== expectedSource) {
        throw new Error("Les données de paie ont changé pendant la saisie. Vos valeurs et vos salariés sélectionnés sont conservés. Rouvrez les opérations en masse après avoir vérifié les données actualisées.");
      }

      const response = await fetch(
        `/api/hr/payroll-processing/periods/${period.id}/variables/bulk`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            "X-Company-Id": String(period.company_id),
          },
          body: JSON.stringify({
            operation: operation.operation,
            criteria: operation.criteria,
            data: operation.data,
            expected_variables_revision: latest.variablesRevision,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.error || errorData.message || "Erreur lors de l'opération en masse"
        );
      }

      const result = await response.json();
      if (!result.success) throw new Error(result.message || result.error || "Erreur lors de l'opération en masse");

      // Recharger les données
      await loadVariables();
      message.success(
        result.message ||
          `Opération effectuée avec succès (${
            result.affected_rows || 0
          } ligne(s) affectée(s))`
      );
      setShowBulkModal(false);
    } catch (error) {
      message.error(
        error instanceof Error
          ? error.message
          : "Erreur lors de l'opération en masse"
      );
      throw error;
    } finally {
      setSaving(false);
    }
  };

  // Nouvelle fonction pour l'ajout rapide de variables
  const handleQuickAddVariable = async (variableData: any) => {
    if (!period?.id) return;

    try {
      setSaving(true);
      const latest = await resource.refresh({ force: true });

      const response = await fetch(
        `/api/hr/payroll-processing/periods/${period.id}/variables`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            "X-Company-Id": String(period.company_id),
          },
          body: JSON.stringify({
            variables: [variableData],
            expected_variables_revision: latest.variablesRevision,
          }),
        }
      );

      const result = await response.json();
      if (!response.ok || !result.success) throw new Error(result.error || result.message || "Erreur lors de l'ajout de la variable");

      // Recharger les données
      await loadVariables();
      message.success("Variable ajoutée avec succès");
      setShowQuickAddModal(false);
      setSelectedEmployee(null);
    } catch (error) {
      throw error;
    } finally {
      setSaving(false);
    }
  };

  // Fonction pour synchroniser les éléments assignés avec les variables de période
  const syncAssignedItemsToVariables = async (employeeId: number) => {
    if (!period?.id) return;

    try {
      setSaving(true);

      const employeeAssigned = employeeAssignedItems.find(
        (item) => item.employee_id === employeeId
      );

      if (!employeeAssigned?.assignedItems.length) {
        message.info("Aucun élément de paie assigné pour cet employé");
        return;
      }

      const employee = periodEmployees.find((emp) => emp.id === employeeId);
      if (!employee) return;

      // Filtrer les éléments actifs avant calcul
      const activeAssignedItems = employeeAssigned.assignedItems.filter(item => item.is_active);
      
      const calculatedValues = await calculateValuesFromAssignedItems(
        activeAssignedItems,
        employee
      );

      // Créer les variables basées sur les éléments assignés
      const variablesToCreate = [];

      // Ajouter les gains calculés
      if (calculatedValues.bonuses > 0) {
        variablesToCreate.push({
          employee_id: employeeId,
          variable_type: "bonus",
          value: calculatedValues.bonuses,
          description: "Calculé depuis les éléments de paie assignés",
        });
      }

      if (calculatedValues.totalAllowances > 0) {
        variablesToCreate.push({
          employee_id: employeeId,
          variable_type: "allowance",
          value: calculatedValues.totalAllowances,
          description: "Calculé depuis les éléments de paie assignés",
        });
      }

      if (calculatedValues.commissions > 0) {
        variablesToCreate.push({
          employee_id: employeeId,
          variable_type: "commission",
          value: calculatedValues.commissions,
          description: "Calculé depuis les éléments de paie assignés",
        });
      }

      // Ajouter les déductions calculées
      if (calculatedValues.advances > 0) {
        variablesToCreate.push({
          employee_id: employeeId,
          variable_type: "advance",
          value: calculatedValues.advances,
          description: "Calculé depuis les éléments de paie assignés",
        });
      }

      if (calculatedValues.loanDeductions > 0) {
        variablesToCreate.push({
          employee_id: employeeId,
          variable_type: "loan_deduction",
          value: calculatedValues.loanDeductions,
          description: "Calculé depuis les éléments de paie assignés",
        });
      }

      if (calculatedValues.otherDeductions > 0) {
        variablesToCreate.push({
          employee_id: employeeId,
          variable_type: "other_deduction",
          value: calculatedValues.otherDeductions,
          description: "Calculé depuis les éléments de paie assignés",
        });
      }

      if (variablesToCreate.length > 0) {
        const response = await fetch(
          `/api/hr/payroll-processing/periods/${period.id}/variables`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              variables: variablesToCreate,
            }),
          }
        );

        if (!response.ok) {
          throw new Error("Erreur lors de la synchronisation");
        }

        await loadVariables();
        message.success(
          `${variablesToCreate.length} variable(s) synchronisée(s) depuis les éléments assignés`
        );
      } else {
        message.info("Aucune variable à synchroniser");
      }
    } catch (error) {
      message.error("Erreur lors de la synchronisation");
    } finally {
      setSaving(false);
    }
  };

  const isEditing = (record: EmployeeVariable) =>
    record.employee_id.toString() === editingKey;

  const edit = (record: EmployeeVariable) => {
    form.setFieldsValue({ ...record });
    setEditingKey(record.employee_id.toString());
  };

  const cancel = () => {
    setEditingKey("");
  };

  const save = async (employeeId: number) => {
    try {
      const row = await form.validateFields();
      const item = employeeVariables.find(employee => employeeId === employee.employee_id);
      if (!item) throw new Error("Employé introuvable dans la préparation");
      const latest = await resource.refresh({ force: true });
      if (JSON.stringify(latest) !== preparedSource.current) {
        throw new Error("Les données de paie ont changé pendant la saisie. Vos valeurs sont conservées. Annulez la ligne, actualisez et vérifiez les montants avant d’enregistrer.");
      }
      await saveEmployeeVariables(employeeId, { ...item, ...row }, item, latest.variablesRevision);
      setEditingKey("");
      await loadVariables();
      message.success("Variables mises à jour avec succès");
    } catch (errInfo) {
      message.error(errInfo instanceof Error ? errInfo.message : "Erreur lors de la validation des données");
    }
  };

  const saveEmployeeVariables = async (
    employeeId: number,
    variables: EmployeeVariable,
    original: EmployeeVariable,
    expectedVariablesRevision?: string
  ) => {
    if (!period?.id) return;

    try {
      setSaving(true);

      // Totals displayed above include assigned items. Save only changed
      // fields, and convert additive totals into their manual complement.
      const fields: Record<string, keyof EmployeeVariable> = {
        attendance_days: 'worked_days', sick_days: 'sick_days', vacation_days: 'vacation_days',
        unpaid_leave: 'unpaid_leave_days', overtime_hours: 'overtime_hours', overtime_rate: 'overtime_rate',
        bonus: 'bonuses', allowance: 'allowances', commission: 'commissions', advance: 'advances',
        loan_deduction: 'loans_deduction', other_deduction: 'other_deductions', dependents: 'dependents',
      };
      const assignedTotal = (category: string) => (original.assigned_payroll_items || [])
        .filter(item => item.is_active && item.type === 'gain' && getActualCategory(item) === category)
        .reduce((sum, item) => sum + Number(item.calculated_value || 0), 0);
      const variablesToSave = [
        {
          employee_id: employeeId,
          variable_type: "attendance_days",
          value: variables.worked_days,
        },
        {
          employee_id: employeeId,
          variable_type: "sick_days",
          value: variables.sick_days,
        },
        {
          employee_id: employeeId,
          variable_type: "vacation_days",
          value: variables.vacation_days,
        },
        {
          employee_id: employeeId,
          variable_type: "unpaid_leave",
          value: variables.unpaid_leave_days,
        },
        {
          employee_id: employeeId,
          variable_type: "overtime_hours",
          value: variables.overtime_hours,
        },
        {
          employee_id: employeeId,
          variable_type: "overtime_rate",
          value: variables.overtime_rate,
        },
        {
          employee_id: employeeId,
          variable_type: "bonus",
          value: variables.bonuses,
        },
        {
          employee_id: employeeId,
          variable_type: "allowance",
          value: variables.allowances,
        },
        {
          employee_id: employeeId,
          variable_type: "commission",
          value: variables.commissions,
        },
        {
          employee_id: employeeId,
          variable_type: "advance",
          value: variables.advances,
        },
        {
          employee_id: employeeId,
          variable_type: "loan_deduction",
          value: variables.loans_deduction,
        },
        {
          employee_id: employeeId,
          variable_type: "other_deduction",
          value: variables.other_deductions,
        },
        {
          employee_id: employeeId,
          variable_type: "dependents",
          value: variables.dependents,
        },
      ].filter(variable => Number(variable.value) !== Number(original[fields[variable.variable_type]]))
        .map(variable => {
          if (!['bonus', 'allowance', 'commission'].includes(variable.variable_type)) return variable;
          const value = Number(variable.value) - assignedTotal(variable.variable_type);
          if (value < 0) throw new Error("Le total saisi ne peut pas être inférieur aux rubriques affectées. Modifiez la rubrique de cette période pour réduire son montant.");
          return { ...variable, value };
        });
      if (!variablesToSave.length) return;

      const response = await fetch(
        `/api/hr/payroll-processing/periods/${period.id}/variables`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            "X-Company-Id": String(period.company_id),
          },
          body: JSON.stringify({
            variables: variablesToSave,
            expected_variables_revision: expectedVariablesRevision,
          }),
        }
      );

      const result = await response.json();
      if (!response.ok || !result.success) throw new Error(result.message || result.error || "Erreur lors de la sauvegarde");
    } catch (error) {
      throw error;
    } finally {
      setSaving(false);
    }
  };

  // NOUVELLE FONCTION : Génération du snapshot complet au clic "Terminer"
  const completeVariablesSetup = async () => {
    if (!period?.id || !dataReady || dataError || loading || saving || editingKey || employeeVariables.length !== periodEmployees.length || !employeeVariables.length) return;

    try {
      setSaving(true);
      const latest = await resource.refresh({ force: true });
      if (JSON.stringify(latest) !== preparedSource.current) throw new Error("Les données de paie ont changé. Actualisez et vérifiez les montants avant de générer le snapshot.");
      message.loading("Génération du snapshot complet des variables...", 0);

      const allVariablesToSave = [];

      // Pour chaque employé, générer le snapshot complet
      for (const employee of employeeVariables) {
        // 1. Variables de présence et temporaires
        const temporaryVariables = [
          {
            employee_id: employee.employee_id,
            variable_type: "attendance_days",
            value: employee.worked_days,
            source_type: "manual",
          },
          {
            employee_id: employee.employee_id,
            variable_type: "sick_days",
            value: employee.sick_days,
            source_type: "manual",
          },
          {
            employee_id: employee.employee_id,
            variable_type: "vacation_days",
            value: employee.vacation_days,
            source_type: "manual",
          },
          {
            employee_id: employee.employee_id,
            variable_type: "unpaid_leave",
            value: employee.unpaid_leave_days,
            source_type: "manual",
          },
          {
            employee_id: employee.employee_id,
            variable_type: "overtime_hours",
            value: employee.overtime_hours,
            source_type: "manual",
          },
          {
            employee_id: employee.employee_id,
            variable_type: "overtime_rate",
            value: employee.overtime_rate,
            source_type: "manual",
          },
          {
            employee_id: employee.employee_id,
            variable_type: "dependents",
            value: employee.dependents,
            source_type: "manual",
          },
        ];

        // 2. Variables calculées depuis les éléments assignés ACTIFS uniquement
        if (
          employee.assigned_payroll_items &&
          employee.assigned_payroll_items.length > 0
        ) {
          // Filtrer explicitement les éléments actifs avant traitement
          const activeItems = employee.assigned_payroll_items.filter(item => item.is_active);
          
          for (const item of activeItems) {
            if (
              item.calculated_value != null &&
              Number.isFinite(Number(item.calculated_value)) && Number(item.calculated_value) >= 0
            ) {
              allVariablesToSave.push({
                employee_id: employee.employee_id,
                payroll_item_id: item.payroll_item_id,
                calculated_amount: item.calculated_value,
                custom_amount: item.custom_amount,
                custom_percentage: item.custom_percentage,
                is_custom: item.custom_amount != null || item.custom_percentage != null ? 1 : 0,
                source_fingerprint: item.source_fingerprint,
                source_type: "calculated",
                calculation_details: JSON.stringify({
                  item_code: item.code,
                  item_name: item.name,
                  calculation_type: item.calculation_type,
                  base_salary: employee.base_salary,
                  calculated_at: new Date().toISOString(),
                }),
                notes: `Snapshot ${period.name} - ${item.name}`,
              });
            }
          }
        }

        // Ajouter les variables temporaires
        allVariablesToSave.push(...temporaryVariables);
      }

      // Appel API pour sauvegarder le snapshot complet
      const response = await fetch(
        `/api/hr/payroll-processing/periods/${period.id}/variables/complete-snapshot`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            "X-Company-Id": String(period?.company_id),
          },
          body: JSON.stringify({
            variables: allVariablesToSave,
            employee_ids: employeeVariables.map(employee => employee.employee_id),
            expected_start_date: period.start_date, expected_end_date: period.end_date,
            expected_variables_revision: latest.variablesRevision,
            snapshot_type: "complete",
            created_by: "variables_management_modal",
            // Envoyer le nouveau statut directement
            status: "variables_set",
          }),
        }
      );

      message.destroy();

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.error || "Erreur lors de la génération du snapshot"
        );
      }

      const result = await response.json();
      if (!result.success) throw new Error(result.error || "Le snapshot n’a pas été enregistré");
      await invalidateBusinessResources(queryClient, resourceScope, "payroll-variables");

      message.success(
        `Snapshot complet généré : ${
          result.data?.total_variables ?? allVariablesToSave.length
        } variables sauvegardées`
      );

      // La mise à jour du statut est maintenant gérée par le backend
      // Il n'est plus nécessaire de faire un deuxième appel.

      // Appeler onSuccess pour fermer le modal et actualiser la vue parent
      onSuccess();
    } catch (error) {
      message.destroy();
      message.error(
        error instanceof Error
          ? error.message
          : "Erreur lors de la génération du snapshot complet"
      );
    } finally {
      setSaving(false);
    }
  };

  const calculateStatistics = () => {
    const totalEmployees = periodEmployees.length;
    const totalWorkedDays = employeeVariables.reduce(
      (sum, emp) => sum + (emp.worked_days || 0),
      0
    );
    const totalOvertimeHours = employeeVariables.reduce(
      (sum, emp) => sum + (emp.overtime_hours || 0),
      0
    );
    const totalBonuses = employeeVariables.reduce(
      (sum, emp) => sum + (emp.bonuses || 0),
      0
    );
    const totalDeductions = employeeVariables.reduce(
      (sum, emp) =>
        sum +
        (emp.advances || 0) +
        (emp.loans_deduction || 0) +
        (emp.other_deductions || 0),
      0
    );

    return {
      totalEmployees,
      avgWorkedDays:
        employeeVariables.length > 0 ? (totalWorkedDays / employeeVariables.length).toFixed(1) : 0,
      totalOvertimeHours,
      totalBonuses,
      totalDeductions,
    };
  };

  if (!period) return null;

  const statistics = calculateStatistics();

  // Colonnes de définition des variables
  const getColumns = () => {
    const baseColumns = [
      {
        title: "Salarié",
        dataIndex: "employee_name",
        key: "employee_name",
        width: isMobile ? 220 : 260,
        fixed: isMobile ? undefined : "left" as const,
        render: (name: string, record: EmployeeVariable) => (
          <div className="payroll-variables__employee">
            <Text strong title={name}>{name}</Text>
            <div className="payroll-variables__employee-meta">
              <Text type="secondary">{record.employee_number}</Text>
              {record.department_name && <>
                <span aria-hidden="true"> · </span>
                <Text type="secondary" title={record.department_name}>{record.department_name}</Text>
              </>}
            </div>
          </div>
        ),
      },
      {
        title: <div>Salaire de base<Text type="secondary" className="payroll-variables__column-hint">Rubriques affectées</Text></div>,
        dataIndex: "base_salary",
        key: "base_salary",
        width: 180,
        align: "right" as const,
        render: (amount: number, record: EmployeeVariable) => {
          const hasBaseSalaryItem = record.assigned_payroll_items?.some(
            (item) => item.is_active && item.code === "SAL_BASE"
          );

          if (!hasBaseSalaryItem || amount === 0) {
            return (
              <div className="payroll-variables__amount">
                <Text type="secondary" className="payroll-variables__column-hint">
                  Aucun SAL_BASE assigné
                </Text>
              </div>
            );
          }

          return (
            <div className="payroll-variables__amount">
              <Text strong className="payroll-variables__base-salary">
                {formatCurrency(amount)}
              </Text>
            </div>
          );
        },
      },
    ];

    const attendanceColumns = [
      {
        title: "Jours Travaillés",
        dataIndex: "worked_days",
        key: "worked_days",
        width: 120,
        editable: true,
        render: (value: number) => <Tag color="blue">{value} jours</Tag>,
      },
      {
        title: "Congés",
        dataIndex: "vacation_days",
        key: "vacation_days",
        width: 100,
        editable: true,
        render: (value: number) =>
          value > 0 ? <Tag color="green">{value}</Tag> : "-",
      },
      {
        title: "Arrêts Maladie",
        dataIndex: "sick_days",
        key: "sick_days",
        width: 120,
        editable: true,
        render: (value: number) =>
          value > 0 ? <Tag color="orange">{value}</Tag> : "-",
      },
      {
        title: "Absences Non Payées",
        dataIndex: "unpaid_leave_days",
        key: "unpaid_leave_days",
        width: 150,
        editable: true,
        render: (value: number) =>
          value > 0 ? <Tag color="red">{value}</Tag> : "-",
      },
    ];

    const overtimeColumns = [
      {
        title: "Heures Sup",
        dataIndex: "overtime_hours",
        key: "overtime_hours",
        width: 120,
        editable: true,
        render: (value: number) =>
          value > 0 ? <Tag color="purple">{value}h</Tag> : "-",
      },
      {
        title: "Taux",
        dataIndex: "overtime_rate",
        key: "overtime_rate",
        width: 100,
        editable: true,
        render: (value: number) => <Tag color="cyan">×{value}</Tag>,
      },
    ];

    const bonusColumns = [
      {
        title: "Primes",
        dataIndex: "bonuses",
        key: "bonuses",
        width: 180,
        align: "right" as const,
        editable: true,
        render: (value: number, record: EmployeeVariable) => {
          const bonusItems =
            record.assigned_payroll_items?.filter(
              (item) => item.is_active && getActualCategory(item) === "bonus"
            ) || [];

          if (value <= 0) {
            return "-";
          }

          return (
            <Button
              type="text"
              className="payroll-variables__amount-detail"
              aria-label={`Voir le détail des primes de ${record.employee_name}`}
              title={`${bonusItems.length} rubrique${bonusItems.length > 1 ? "s" : ""} affectée${bonusItems.length > 1 ? "s" : ""}`}
              onClick={() => {
                setSelectedEmployeeForDetail(record);
                setShowBonusDetailModal(true);
              }}
            >
              <span>{formatCurrency(Number(value) || 0)}</span>
              <RightOutlined />
            </Button>
          );
        },
      },
      {
        title: "Indemnités",
        dataIndex: "allowances",
        key: "allowances",
        width: 180,
        align: "right" as const,
        editable: true,
        render: (value: number, record: EmployeeVariable) => {
          const allowanceItems =
            record.assigned_payroll_items?.filter(
              (item) =>
                item.is_active && getActualCategory(item) === "allowance"
            ) || [];

          if (value <= 0) {
            return "-";
          }

          return (
            <Button
              type="text"
              className="payroll-variables__amount-detail"
              aria-label={`Voir le détail des indemnités de ${record.employee_name}`}
              title={`${allowanceItems.length} rubrique${allowanceItems.length > 1 ? "s" : ""} affectée${allowanceItems.length > 1 ? "s" : ""}`}
              onClick={() => {
                setSelectedEmployeeForDetail(record);
                setShowAllowancesDetailModal(true);
              }}
            >
              <span>{formatCurrency(Number(value) || 0)}</span>
              <RightOutlined />
            </Button>
          );
        },
      },
      {
        title: "Commissions",
        dataIndex: "commissions",
        key: "commissions",
        width: 160,
        align: "right" as const,
        editable: true,
        render: (value: number) =>
          value > 0 ? (
            <Text className="payroll-variables__gain">{formatCurrency(Number(value) || 0)}</Text>
          ) : (
            "-"
          ),
      },
    ];

    const deductionColumns = [
      {
        title: "Avances",
        dataIndex: "advances",
        key: "advances",
        width: 120,
        editable: true,
        render: (value: number) =>
          value > 0 ? (
            <Text className="payroll-variables__deduction">-{formatCurrency(Number(value) || 0)}</Text>
          ) : (
            "-"
          ),
      },
      {
        title: "Prêts",
        dataIndex: "loans_deduction",
        key: "loans_deduction",
        width: 120,
        editable: true,
        render: (value: number) =>
          value > 0 ? (
            <Text className="payroll-variables__deduction">-{formatCurrency(Number(value) || 0)}</Text>
          ) : (
            "-"
          ),
      },
      {
        title: "Autres Retenues",
        dataIndex: "other_deductions",
        key: "other_deductions",
        width: 130,
        editable: true,
        render: (value: number) =>
          value > 0 ? (
            <Text className="payroll-variables__deduction">-{formatCurrency(Number(value) || 0)}</Text>
          ) : (
            "-"
          ),
      },
    ];

    const actionColumn = {
      title: "Actions",
      key: "actions",
      className: "payroll-variables__actions-cell",
      width: 58,
      align: "center" as const,
      fixed: isMobile ? undefined : "right" as const,
      render: (_: any, record: EmployeeVariable) => {
        const editable = isEditing(record);
        const employee = periodEmployees.find(emp => Number(emp.employee_id ?? emp.id) === Number(record.employee_id));
        return (
          <RowActionsMenu
            ariaLabel={`Actions de ${record.employee_name}`}
            loading={editable && saving}
            items={editable ? [
              { key: "save", label: "Enregistrer les variables", icon: <SaveOutlined />, disabled: saving, onClick: () => { void save(record.employee_id); } },
              { key: "cancel", label: "Annuler la saisie", icon: <CloseOutlined />, disabled: saving, onClick: cancel },
            ] : [
              { key: "edit", label: "Modifier", icon: <EditOutlined />, disabled: editingKey !== "" || loading || saving, onClick: () => edit(record) },
              { key: "add", label: "Ajouter une variable", icon: <PlusOutlined />, disabled: !employee || editingKey !== "" || loading || saving, onClick: () => {
                if (!employee) return;
                setSelectedEmployee(employee);
                setQuickAddDefaults({});
                setShowQuickAddModal(true);
              } },
              { key: "payslip", label: "Bulletin IUTS", icon: <FileTextOutlined />, disabled: loading || saving, onClick: () => {
                setSelectedEmployeeForPayslip(record);
                setShowPayslipModal(true);
              } },
            ]}
          />
        );
      },
    };

    // Retourner les colonnes selon l'onglet actif
    switch (activeTab) {
      case "attendance":
        return [...baseColumns, ...attendanceColumns, actionColumn];
      case "overtime":
        return [...baseColumns, ...overtimeColumns, actionColumn];
      case "bonuses":
        return [...baseColumns, ...bonusColumns, actionColumn];
      case "deductions":
        return [...baseColumns, ...deductionColumns, actionColumn];
      default:
        return [
          ...baseColumns,
          ...attendanceColumns.slice(0, 1), // Juste jours travaillés
          ...overtimeColumns.slice(0, 1), // Juste heures sup
          ...bonusColumns.slice(0, 1), // Juste primes
          ...deductionColumns.slice(0, 1), // Juste avances
          actionColumn,
        ];
    }
  };

  const columns = getColumns().map((col: any) => {
    if (!col.editable) {
      return col;
    }
    return {
      ...col,
      onCell: (record: EmployeeVariable) => ({
        record,
        inputType:
          typeof record[col.dataIndex as keyof EmployeeVariable] === "number"
            ? "number"
            : "text",
        dataIndex: col.dataIndex,
        title: col.title,
        editing: isEditing(record),
      }),
    };
  });

  const EditableCell: React.FC<any> = ({
    editing,
    dataIndex,
    title,
    inputType,
    record,
    index,
    children,
    ...restProps
  }) => {
    if (!editing) return <td {...restProps}>{children}</td>;

    const inputNode =
      inputType === "number"
        ? <InputNumber min={0} step={0.1} aria-label={`${title} de ${record.employee_name}`} />
        : <Input aria-label={`${title} de ${record.employee_name}`} />;

    return (
      <td {...restProps}>
        <Form.Item
          name={dataIndex}
          style={{ margin: 0 }}
          rules={[
            {
              required: dataIndex === "worked_days",
              message: `Veuillez saisir ${title}!`,
            },
          ]}
        >
          {inputNode}
        </Form.Item>
      </td>
    );
  };

  const canComplete = !editingKey && !loading && !saving && dataReady && !dataError &&
    employeeVariables.length > 0 && employeeVariables.length === periodEmployees.length;
  const statisticsVerified = dataReady && !dataError && !loading;
  const tabTitles: Record<string, string> = {
    overview: "Vue d’ensemble", attendance: "Présences", overtime: "Heures supplémentaires",
    bonuses: "Primes & indemnités", deductions: "Déductions",
  };
  const tabHelp: Record<string, string> = {
    overview: "Consultez les variables de chaque salarié avant d’enregistrer la préparation de la période.",
    attendance: "Saisissez les jours travaillés, congés, arrêts maladie et absences non payées.",
    overtime: "Renseignez les heures supplémentaires et leur taux de majoration. Le taux par défaut est 1,5 (150 %).",
    bonuses: "Les totaux incluent les rubriques affectées et les compléments de cette période. Consultez le détail depuis les montants.",
    deductions: "Renseignez les avances sur salaire, remboursements de prêts et autres retenues de la période.",
  };
  const completionHelp = editingKey
    ? "Enregistrez ou annulez la saisie du salarié pour terminer."
    : saving ? "Enregistrement en cours."
    : loading ? "Les variables sont en cours d’actualisation."
    : dataError ? "Résolvez les anomalies signalées avant d’enregistrer."
    : !employeeVariables.length ? "Les variables des salariés doivent être disponibles avant l’enregistrement."
    : !dataReady ? "Actualisez les données avant d’enregistrer la préparation."
    : "L’enregistrement prépare les variables pour le calcul de la paie.";
  const metricItems = [
    { key: "employees", label: "Salariés", icon: <TeamOutlined />, value: statisticsVerified ? statistics.totalEmployees : "—" },
    { key: "days", label: "Jours moyens", icon: <CalendarOutlined />, value: statisticsVerified ? `${Number(statistics.avgWorkedDays).toLocaleString("fr-FR", { minimumFractionDigits: 1, maximumFractionDigits: 1 })} j` : "—" },
    { key: "overtime", label: "Heures supplémentaires", icon: <ClockCircleOutlined />, value: statisticsVerified ? `${statistics.totalOvertimeHours} h` : "—" },
    { key: "bonuses", label: "Primes", icon: <GiftOutlined />, value: statisticsVerified ? formatCurrency(statistics.totalBonuses) : "—", gain: true },
    { key: "deductions", label: "Déductions", icon: <MinusCircleOutlined />, value: statisticsVerified ? formatCurrency(statistics.totalDeductions) : "—" },
  ];
  const modalStyle = {
    top: expanded ? 12 : 20,
    "--payroll-text": themeToken.colorText,
    "--payroll-secondary": themeToken.colorTextSecondary,
    "--payroll-border": themeToken.colorBorderSecondary,
    "--payroll-surface": themeToken.colorBgContainer,
    "--payroll-fill": themeToken.colorFillAlter,
    "--payroll-accent": themeToken.colorPrimaryText,
    "--payroll-gain": themeToken.colorSuccessText,
    "--payroll-error": themeToken.colorErrorText,
    "--payroll-editing": themeToken.colorPrimaryBg,
    "--text-secondary": themeToken.colorTextSecondary,
    "--color-primary": themeToken.colorPrimaryText,
  } as React.CSSProperties;
  const tableMinWidth = columns.reduce((width: number, column: any) => width + (Number(column.width) || 120), 0);

  return (
    <>
      <Modal
        title={
          <div className="payroll-variables__header">
            <div className="payroll-variables__heading">
              <BarChartOutlined />
              <div>
                <Title level={4}>Variables de paie</Title>
                <Text type="secondary">Vérifier les variables avant le calcul</Text>
              </div>
            </div>
            <div className="payroll-variables__period">
              <CalendarOutlined />
              <div>
                <Text strong>{period.name}</Text>
                <Text type="secondary">{formatPeriod(period.start_date, period.end_date)}</Text>
              </div>
            </div>
            <Tag color="blue" className="payroll-variables__status">{getPeriodStatusLabel(period)}</Tag>
            <Button
              icon={expanded ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
              onClick={() => setExpanded(value => !value)}
              aria-label={expanded ? "Réduire la fenêtre" : "Agrandir la fenêtre"}
              aria-pressed={expanded}
              className="payroll-variables__expand"
            >
              {expanded ? "Réduire" : "Agrandir"}
            </Button>
          </div>
        }
        open={visible}
        onCancel={onCancel}
        width={expanded ? "calc(100vw - 24px)" : "min(1760px, calc(100vw - 40px))"}
        className={`payroll-variables-dialog${expanded ? " payroll-variables-dialog--expanded" : ""}`}
        style={modalStyle}
        footer={
          <div className="payroll-variables__footer">
            <div className="payroll-variables__completion" aria-live="polite">
              <InfoCircleOutlined />
              <div>
                {statisticsVerified && <Text strong>Variables de paie préparées</Text>}
                <Text type="secondary">{completionHelp}</Text>
              </div>
            </div>
            <div className="payroll-variables__footer-actions">
              <Button onClick={onCancel}>Fermer</Button>
              <Button type="primary" onClick={completeVariablesSetup} loading={saving}
                disabled={!canComplete} icon={<SaveOutlined />}>
                Enregistrer la préparation
              </Button>
            </div>
          </div>
        }
      >
        <section className="payroll-variables-workspace">
          <div className="payroll-variables__metrics" aria-label="Indicateurs de la période">
            {metricItems.map(metric => (
              <div key={metric.key} className={`payroll-variables__metric${metric.gain ? " payroll-variables__metric--gain" : ""}`}>
                {metric.icon}
                <div>
                  <span className="payroll-variables__metric-label">{metric.label}</span>
                  <strong title={String(metric.value)}>{metric.value}</strong>
                </div>
              </div>
            ))}
          </div>

          {(dataError || employeeVariables.some(employee => employee.unpaid_leave_days > 0)) && (
            <div className="payroll-variables__alerts">
              {dataError && <Alert type="error" showIcon message="Préparation de paie bloquée" description={
                <Space direction="vertical" style={{ width: "100%" }}>
                  <span>{dataError}</span>
                  {preparationIssues.map(issue => <div key={issue.employee.employee_id}>
                    <Space wrap>
                      <Text strong>{issue.employee.first_name} {issue.employee.last_name}</Text>
                      <Text>{issue.employee.employee_number}</Text>
                      {issue.variable_type && <Button size="small"
                        disabled={loading || saving || editingKey !== "" || resource.status !== "verified"}
                        aria-label={`${issue.code === "HISTORICAL_EMPLOYEE_CONTEXT_REQUIRED" ? "Renseigner le profil" : issue.variable_type === "salaire_base" ? "Renseigner le salaire" : "Renseigner la variable"} de ${issue.employee.first_name} ${issue.employee.last_name}`}
                        onClick={() => { setSelectedEmployee(issue.employee); setQuickAddDefaults(issue); setShowQuickAddModal(true); }}>
                        {issue.code === "HISTORICAL_EMPLOYEE_CONTEXT_REQUIRED" ? "Renseigner le profil de la période" : issue.variable_type === "salaire_base" ? "Renseigner le salaire de la période" : "Renseigner la variable de la période"}
                      </Button>}
                    </Space>
                    <div>{issue.message}</div>
                  </div>)}
                </Space>
              } />}
              {employeeVariables.some(employee => employee.unpaid_leave_days > 0) && <Alert type="info" showIcon
                message="Absences non payées"
                description="Le calcul applique une base de 30 jours au salaire de base, au sursalaire et à la prime d’ancienneté. Les indemnités mensuelles sont maintenues." />}
            </div>
          )}

          <Tabs activeKey={activeTab} onChange={setActiveTab} size="small" className="payroll-variables__tabs"
            items={[
              { key: "overview", label: <span><UserOutlined /> Vue d’ensemble</span> },
              { key: "attendance", label: <span><ClockCircleOutlined /> Présences</span> },
              { key: "overtime", label: <span><ClockCircleOutlined /> Heures sup.</span> },
              { key: "bonuses", label: <span><GiftOutlined /> Primes & indemnités</span> },
              { key: "deductions", label: <span><MinusCircleOutlined /> Déductions</span> },
            ]}
          />

          <div className="payroll-variables__toolbar">
            <Title level={5} id="payroll-variables-table-title">{tabTitles[activeTab]}</Title>
            <Space wrap size={8}>
              <Button icon={<BulbOutlined />}
                onClick={() => { bulkSource.current = preparedSource.current; setShowBulkModal(true); }}
                disabled={Boolean(editingKey) || !dataReady || Boolean(dataError) || loading || saving}>
                Opérations en masse
              </Button>
              <Button icon={<ReloadOutlined />} onClick={loadData} loading={loading}>Actualiser</Button>
              <Tooltip title="L’export des variables n’est pas encore disponible">
                <Button icon={<DownloadOutlined />} disabled>Exporter · à venir</Button>
              </Tooltip>
            </Space>
          </div>

          <div ref={tableViewportRef} className="payroll-variables__table" role="region" aria-labelledby="payroll-variables-table-title">
            <Form form={form} component={false}>
              <Table
                components={{ body: { cell: EditableCell } }}
                dataSource={employeeVariables}
                columns={columns}
                rowClassName={record => isEditing(record) ? "editable-row payroll-variables__row--editing" : "editable-row"}
                rowKey="employee_id"
                loading={loading}
                size="small"
                tableLayout="fixed"
                scroll={{ x: tableMinWidth, y: tableBodyHeight }}
                pagination={false}
                locale={{ emptyText: dataError ? "Les données de la période doivent être complétées ou actualisées." : "Aucun salarié préparé pour cette période." }}
              />
            </Form>
          </div>

          <div className="payroll-variables__list-meta">
            <Text type="secondary">{employeeVariables.length} salarié{employeeVariables.length > 1 ? "s" : ""} · Liste continue</Text>
            {preparationIssues.length > 0 && <Text type="danger">{preparationIssues.length} préparation{preparationIssues.length > 1 ? "s" : ""} à compléter</Text>}
          </div>

          <Collapse size="small" bordered={false} className="payroll-variables__details"
            items={[{
              key: "rules", label: "Détail des rubriques et règles de saisie",
              children: <div>
                <p>{tabHelp[activeTab]}</p>
                <p>Les modifications concernent cette période. Pour réduire une rubrique affectée, modifiez son montant pour cette période.</p>
              </div>,
            }]}
          />
        </section>
      </Modal>

      {/* Modal pour opérations en masse */}
      <BulkVariablesModal
        visible={showBulkModal}
        period={period}
        employees={periodEmployees}
        onCancel={() => setShowBulkModal(false)}
        onApply={handleBulkOperation}
      />

      {/* Modal pour ajout rapide de variables */}
      <QuickAddVariableModal
        visible={showQuickAddModal}
        employee={selectedEmployee}
        payrollItems={payrollItems}
        initialVariableType={quickAddDefaults.variable_type}
        initialPayrollItemId={quickAddDefaults.payroll_item_id}
        historicalProfileRequired={quickAddDefaults.code === 'HISTORICAL_EMPLOYEE_CONTEXT_REQUIRED'}
        periodEndDate={period.end_date}
        periodName={period.name}
        onCancel={() => {
          setShowQuickAddModal(false);
          setSelectedEmployee(null);
        }}
        onSave={handleQuickAddVariable}
      />

      {/* Modal de détail des primes */}
      <Modal
        title={`Détail des primes - ${
          selectedEmployeeForDetail?.employee_name || ""
        }`}
        visible={showBonusDetailModal}
        onCancel={() => {
          setShowBonusDetailModal(false);
          setSelectedEmployeeForDetail(null);
        }}
        footer={[
          <Button
            key="close"
            onClick={() => {
              setShowBonusDetailModal(false);
              setSelectedEmployeeForDetail(null);
            }}
          >
            Fermer
          </Button>,
        ]}
        width={600}
      >
        {selectedEmployeeForDetail && (
          <div>
            <Table
              columns={[
                {
                  title: "Code",
                  dataIndex: "code",
                  key: "code",
                  width: 100,
                  render: (code: string) => <Tag color="blue">{code}</Tag>,
                },
                {
                  title: "Nom",
                  dataIndex: "name",
                  key: "name",
                },
                {
                  title: "Type de Calcul",
                  dataIndex: "calculation_type",
                  key: "calculation_type",
                  render: (type: string) => (
                    <Tag
                      color={
                        type === "fixed"
                          ? "green"
                          : type === "percentage"
                          ? "orange"
                          : "purple"
                      }
                    >
                      {type === "fixed"
                        ? "Fixe"
                        : type === "percentage"
                        ? "Pourcentage"
                        : "Formule"}
                    </Tag>
                  ),
                },
                {
                  title: "Montant Calculé",
                  key: "calculated_value",
                  render: (_: any, record: AssignedPayrollItem) => (
                    <Text strong style={{ color: "#52c41a" }}>
                      {formatCurrency(record.calculated_value || 0)}
                    </Text>
                  ),
                },
              ]}
              dataSource={
                selectedEmployeeForDetail.assigned_payroll_items?.filter(
                  (item) =>
                    item.is_active && getActualCategory(item) === "bonus"
                ) || []
              }
              rowKey="id"
              pagination={false}
              size="small"
            />
            <div style={{ marginTop: 16, textAlign: "right" }}>
              <Text strong>
                Total des primes :{" "}
                <span style={{ color: "#52c41a", fontSize: "16px" }}>
                  {formatCurrency(Number(selectedEmployeeForDetail.bonuses) || 0)}
                </span>
              </Text>
            </div>
          </div>
        )}
      </Modal>

      {/* Modal de détail des indemnités */}
      <Modal
        title={`Détail des indemnités - ${
          selectedEmployeeForDetail?.employee_name || ""
        }`}
        visible={showAllowancesDetailModal}
        onCancel={() => {
          setShowAllowancesDetailModal(false);
          setSelectedEmployeeForDetail(null);
        }}
        footer={[
          <Button
            key="close"
            onClick={() => {
              setShowAllowancesDetailModal(false);
              setSelectedEmployeeForDetail(null);
            }}
          >
            Fermer
          </Button>,
        ]}
        width={600}
      >
        {selectedEmployeeForDetail && (
          <div>
            <Table
              columns={[
                {
                  title: "Code",
                  dataIndex: "code",
                  key: "code",
                  width: 100,
                  render: (code: string) => <Tag color="cyan">{code}</Tag>,
                },
                {
                  title: "Nom",
                  dataIndex: "name",
                  key: "name",
                },
                {
                  title: "Type de Calcul",
                  dataIndex: "calculation_type",
                  key: "calculation_type",
                  render: (type: string) => (
                    <Tag
                      color={
                        type === "fixed"
                          ? "green"
                          : type === "percentage"
                          ? "orange"
                          : "purple"
                      }
                    >
                      {type === "fixed"
                        ? "Fixe"
                        : type === "percentage"
                        ? "Pourcentage"
                        : "Formule"}
                    </Tag>
                  ),
                },
                {
                  title: "Montant Calculé",
                  key: "calculated_value",
                  render: (_: any, record: AssignedPayrollItem) => (
                    <Text strong style={{ color: "#52c41a" }}>
                      {formatCurrency(record.calculated_value || 0)}
                    </Text>
                  ),
                },
              ]}
              dataSource={
                selectedEmployeeForDetail.assigned_payroll_items?.filter(
                  (item) =>
                    item.is_active && getActualCategory(item) === "allowance"
                ) || []
              }
              rowKey="id"
              pagination={false}
              size="small"
            />
            <div style={{ marginTop: 16, textAlign: "right" }}>
              <Text strong>
                Total des indemnités :{" "}
                <span style={{ color: "#52c41a", fontSize: "16px" }}>
                  {formatCurrency(Number(selectedEmployeeForDetail.allowances) || 0)}
                </span>
              </Text>
            </div>
          </div>
        )}
      </Modal>

      {/* Modal de bulletin IUTS */}
      {selectedEmployeeForPayslip && (
        <PayslipDetailsModal
          visible={showPayslipModal}
          period={period}
          employeeId={selectedEmployeeForPayslip.employee_id}
          onCancel={() => {
            setShowPayslipModal(false);
            setSelectedEmployeeForPayslip(null);
          }}
        />
      )}
    </>
  );
};

export default VariableManagementModal;
